import React from 'react';
import { ShieldCheck, Layers, Globe, Zap, Heart, CheckCircle, Trophy, Handshake } from 'lucide-react';
import SEO from './seo/SEO';

const AboutPage = () => {
  const reasons = [
    { icon: ShieldCheck, title: 'Ethical & Transparent Practices', desc: 'We follow strict ethical standards and regulatory compliance in recruitment, outsourcing, and travel services.' },
    { icon: Layers, title: 'Integrated Service Expertise', desc: 'From recruitment and outsourcing to travel and tourism support, we offer end-to-end solutions under one trusted brand.' },
    { icon: Globe, title: 'Global Reach, Local Knowledge', desc: 'With strong networks across India, South Asia, and Africa, we deliver reliable, region-aware services.' },
    { icon: Zap, title: 'Efficiency & Reliability', desc: 'We ensure timely talent delivery, accurate payroll processing, and seamless travel coordination.' },
    { icon: Heart, title: 'Client-Focused Approach', desc: 'We work as a strategic partner, providing customized, cost-effective solutions aligned with client needs.' },
  ];

  const values = [
    { name: 'Integrity', desc: 'Ethical and transparent recruitment practices' },
    { name: 'Excellence', desc: 'Delivering the best talent and solutions' },
    { name: 'Commitment', desc: 'Supporting employers and candidates at every step' },
    { name: 'Respect', desc: 'Treating people with dignity and fairness' },
    { name: 'Innovation', desc: 'Modern and efficient recruitment solutions' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden">
      <SEO
        title="About GlobalEPRC | Global Ethical People Resourcing Consultancy"
        description="Learn why organizations choose GlobalEPRC (Global Ethical People Resourcing Consultancy) for ethical, integrated recruitment, outsourcing, IT, training and travel services."
        path="/about"
      />
      {/* 1. Global CSS for Animations */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          display: inline-block;
          animation: marquee 30s linear infinite;
        }
        .bg-gold-gradient {
          background: linear-gradient(to right, #FDE68A 0%, #D4AF37 40%, #B45309 100%);
        }
        .text-gold-gradient {
          background: linear-gradient(to right, #FDE68A 0%, #D4AF37 40%, #B45309 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .gold-shadow {
          box-shadow: 0 10px 15px -3px rgba(180, 83, 9, 0.2);
        }

@keyframes marquee {
    0% { transform: translateX(0%); }
    100% { transform: translateX(-50%); }
  }
  
  /* Smoother animation specifically for logos */
  @keyframes scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(calc(-250px * 6)); } /* Adjust based on logo width * count */
  }

  .animate-marquee {
    display: inline-block;
    animation: marquee 30s linear infinite;
  }

  .animate-scroll {
    animation: marquee 25s linear infinite;
    display: flex;
    width: max-content;
  }

  .bg-gold-gradient {
    background: linear-gradient(to right, #FDE68A 0%, #D4AF37 40%, #B45309 100%);
  }
  
  .text-gold-gradient {
    background: linear-gradient(to right, #FDE68A 0%, #D4AF37 40%, #B45309 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

      `}</style>
      {/* 4. Hero Section */}
      <section className="pt-48 pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-1.5 rounded-full border border-amber-100 mb-6 uppercase tracking-widest text-xs font-bold">
              <Trophy className="h-4 w-4" />
              Our Journey & Purpose
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-8 leading-tight">
              Why Choose GlobalEPRC <br />
              {/* <span className="text-gold-gradient">Resourcing Consultancy?</span> */}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed italic border-l-4 border-amber-500 pl-6 text-left max-w-3xl">
              "GlobalEPRC (GEPRC) redefine the word of recruitment, transcending boundaries between IT and non-IT sectors. We go beyond recruitment, unlocking the full potential of global industries."
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:gap-14 lg:px-10">
          <div className="grid min-h-[350px] grid-cols-12 grid-rows-[180px_150px] gap-3 sm:min-h-[430px] sm:grid-rows-[220px_190px]" aria-label="GlobalEPRC service areas">
            <img src="/1.jpeg" alt="Facilities team delivering workplace support" className="col-span-7 row-span-2 h-full w-full rounded-xl object-cover shadow-md" />
            <img src="/2.jpeg" alt="Skilled workers supporting industrial projects" className="col-span-5 h-full w-full rounded-xl object-cover shadow-md" />
            <div className="col-span-5 grid h-full grid-cols-2 gap-3">
              <img src="/3.jpeg" alt="Travel and tourism services across global destinations" className="h-full w-full rounded-xl object-cover shadow-md" />
              <img src="/outsourcing.jpeg" alt="Business process outsourcing and people services" className="h-full w-full rounded-xl object-cover shadow-md" />
            </div>
          </div>
          <div>
            <span className="inline-flex rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[.2em] text-amber-600">Who We Are</span>
            <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">Redefining the World of <span className="text-gold-gradient">Global Recruitment</span></h2>
            <p className="mt-4 text-sm leading-6 text-slate-600">GlobalEPRC (GEPRC) redefines recruitment across IT and non-IT sectors. We go beyond recruitment, connecting skilled people with organizations and helping businesses build capable teams across industries and borders.</p>
            <blockquote className="mt-4 border-l-[3px] border-amber-400 pl-4 text-sm font-semibold italic leading-5 text-slate-700">We provide end-to-end people and outsourcing services that help organizations streamline operations and focus on sustainable business growth.</blockquote>
            <p className="mt-4 text-sm leading-6 text-slate-600">We support continuous skill development so candidates can meet evolving workplace needs, and offer travel and tourism services designed around safe, smooth workforce mobility.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <article className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm"><Trophy className="h-7 w-7 rounded-lg bg-amber-50 p-1.5 text-amber-600" /><h3 className="mt-3 text-sm font-bold text-slate-900">Our Mission</h3><p className="mt-1 text-xs leading-5 text-slate-600">To connect organizations with qualified people through ethical, dependable services.</p></article>
              <article className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm"><Globe className="h-7 w-7 rounded-lg bg-amber-50 p-1.5 text-amber-600" /><h3 className="mt-3 text-sm font-bold text-slate-900">Our Vision</h3><p className="mt-1 text-xs leading-5 text-slate-600">To be a trusted global partner for ethical recruitment and people solutions.</p></article>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Stats Section */}
      <section className="bg-slate-50 py-16 border-y border-slate-100">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {[
{ label: 'Building Confidence', val: 'Trusted Partner', icon: Handshake },              // { label: 'Industries Covered', val: 'IT & Non-IT', icon: Briefcase },
              { label: 'Ethics Rating', val: '100% Compliance', icon: ShieldCheck },
              { label: 'Support', val: '24/7 Service', icon: Zap },
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center text-center group">
                <stat.icon className="h-8 w-8 text-amber-600 mb-4 group-hover:scale-110 transition-transform" />
                <h4 className="text-2xl font-bold text-slate-900">{stat.val}</h4>
                <p className="text-slate-500 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5.5 Trusted Partners Marquee - NEW SECTION */}
{/* 5.5 Trusted Partners Logo Section */}
{/* <section className="py-20 bg-white border-b border-slate-100">
  <div className="container mx-auto px-4 text-center mb-12">
    <span className="text-amber-600 font-bold tracking-[0.3em] uppercase text-xs">Global Network</span>
    <h3 className="text-3xl font-bold text-slate-900 mt-2">Trusted by Industry Leaders</h3>
  </div>

  <div className="relative flex overflow-hidden bg-white py-4">
    {/* The 'animate-scroll' class is the key here */}
    {/* <div className="animate-scroll flex items-center"> */}
      {/* First Set of Logos */}
      {/* {[
        "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg",
        "https://upload.wikimedia.org/wikipedia/commons/a/a0/Wipro_Primary_Logo_Color_RGB.svg",
        "https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg",
        "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg",
        "https://upload.wikimedia.org/wikipedia/commons/9/95/Infosys_logo.svg",
        "https://upload.wikimedia.org/wikipedia/commons/b/b1/Tata_Consultancy_Services_Logo.svg"
      ].map((logo, index) => (
        <div key={index} className="flex-shrink-0 w-[250px] px-10 flex justify-center items-center grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-500">
          <img src={logo} alt="Partner Logo" className="h-12 w-auto object-contain" />
        </div>
      ))}
      
      {/* Duplicate Set for infinite loop effect */}
      {/* {[
        "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg",
        "https://upload.wikimedia.org/wikipedia/commons/a/a0/Wipro_Primary_Logo_Color_RGB.svg",
        "https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg",
        "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg",
        "https://upload.wikimedia.org/wikipedia/commons/9/95/Infosys_logo.svg",
        "https://upload.wikimedia.org/wikipedia/commons/b/b1/Tata_Consultancy_Services_Logo.svg"
      ].map((logo, index) => (
        <div key={`dup-${index}`} className="flex-shrink-0 w-[250px] px-10 flex justify-center items-center grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-500">
          <img src={logo} alt="Partner Logo" className="h-12 w-auto object-contain" />
        </div>
      ))}
    </div>
  </div> */} 
 {/* </section> */}

{/* working sor scrooll */}

      

      {/* Why Us Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-[.95fr_1.05fr] lg:gap-16 lg:px-10">
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-amber-100 via-white to-blue-100" />
            <img src="/About US 1.png" alt="GlobalEPRC recruitment, global reach, and people values" className="relative w-full rounded-[1.5rem] border border-white object-cover shadow-xl shadow-slate-900/10" />
            <div className="absolute -bottom-5 left-5 flex items-center gap-3 rounded-xl bg-slate-950 px-4 py-3 text-white shadow-lg sm:left-8">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-400 text-slate-950"><Globe className="h-5 w-5" /></span>
              <span><span className="block text-[10px] font-bold uppercase tracking-[.16em] text-amber-300">Our Reach</span><span className="mt-0.5 block text-sm font-semibold">Global perspective. Local care.</span></span>
            </div>
          </div>

          <div className="pt-5 lg:pt-0">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.22em] text-amber-700"><span className="h-px w-8 bg-amber-500" /> The GlobalEPRC Difference</span>
            <h2 className="mt-3 max-w-xl text-3xl font-black leading-tight text-slate-900 sm:text-4xl">A trusted partner for people, growth, and progress</h2>
            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600">We bring people-first thinking and practical expertise to every partnership, with services shaped around your goals.</p>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {reasons.map(({ icon: Icon, title, desc }, idx) => (
                <article key={title} className={`group rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-amber-200 hover:shadow-lg ${idx === reasons.length - 1 ? 'sm:col-span-2' : ''}`}>
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700 transition group-hover:bg-amber-500 group-hover:text-white"><Icon className="h-5 w-5" /></span>
                    <div><h3 className="text-sm font-bold text-slate-900">{title}</h3><p className="mt-1.5 text-xs leading-5 text-slate-600">{desc}</p></div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Art of Sourcing Section */}


      {/* 7. Our Values */}
      <section className="container mx-auto px-4 md:px-8 pb-24">
        <div className="bg-slate-950 rounded-[3rem] p-10 md:p-20 relative overflow-hidden text-white shadow-2xl">
          <div className="absolute top-0 right-0 w-80 h-80 bg-gold-gradient opacity-10 rounded-full -mr-40 -mt-40 blur-[80px]"></div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-16">
              <div className="h-1 w-12 bg-amber-500"></div>
              <h2 className="text-3xl font-bold uppercase tracking-widest">Our Values</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {values.map((val, idx) => (
                <div key={idx} className="group p-8 rounded-3xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="text-amber-500 h-6 w-6" />
                    <h5 className="text-2xl font-bold text-amber-100">{val.name}</h5>
                  </div>
                  <p className="text-slate-400 leading-relaxed italic">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 8. Footer Placeholder */}
    </div>
  );
};

export default AboutPage;
